<script setup lang="ts">
import { computed, unref } from 'vue';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';
import { useCustomFormsStorageContext } from '@satellite/composables/useCustomFormsStorageContext';

import MultidocumentUploadButton from '../MultidocumentUploadButton.vue';
import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Multi-document field type - multiple file uploads.
 * Requires storage context via CUSTOM_FORMS_STORAGE_CONTEXT_KEY (warehouseId, storageEndpoint).
 */
const props = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string[]];
  /** @deprecated Use update:model-value instead */
  input: [value: string[]];
}>();

const { normalizedProps } = useCustomFieldProps(props);
const storageContext = useCustomFormsStorageContext();
const { rules: validationRules } = useCustomFieldValidation(computed(() => props.field));

const modelValueArray = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));

function handleUpdate(value: string[]) {
  emit('update:model-value', value);
  emit('input', value);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <div class="custom-field-multidocument">
      <multidocument-upload-button
        :warehouse-id="unref(storageContext).warehouseId"
        :storage-endpoint="unref(storageContext).storageEndpoint"
        :model-value="unref(modelValueArray)"
        :required="normalizedProps.required"
        :rules="validationRules"
        :read-only="normalizedProps.readOnly"
        :label="field.label ?? 'Upload documents'"
        :placeholder="field.placeholder"
        :variant="normalizedProps.outlined ? 'simple' : 'default'"
        @update:model-value="handleUpdate" />
    </div>
  </custom-field-input-base>
</template>

<style scoped lang="scss">
.custom-field-multidocument {
  width: 100%;
}
</style>
